import { useCallback, useEffect, type RefObject } from "react";

/** The tier at which `genre-pack.module.css` releases GP12's clamp (its
 *  `.collapse[data-cl-fits] .collapseBody` rule). A media query cannot be read
 *  out of a stylesheet, so the spec holds the two copies together. */
export const CLAMP_RELEASE_QUERY = "(min-width: 735px)";

/** True where the stylesheet has released the clamp, so nothing is clipped and
 *  a measurement would force layout for an answer nothing uses. */
export function clampReleased(): boolean {
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(CLAMP_RELEASE_QUERY).matches;
}

/** `fn` at most once per frame however often it is asked for — a pack's logos
 *  arrive one `load` at a time, and each read would force its own layout. */
export function frameBatched(fn: () => void): { schedule: () => void; cancel: () => void } {
  let frame = 0;
  return {
    schedule: () => {
      if (frame === 0) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          fn();
        });
      }
    },
    cancel: () => {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  };
}

/**
 * GP12 — "a pack whose logos are taller than 475px is cut to 475px, with a Show More / Show Less
 * button to expand and collapse it." This hook is the measurement and nothing else: it reports
 * whether `ref`'s content is taller than `limitPx` to `report`, and re-reports whenever that
 * could have changed. It holds no state: the answer only feeds an attribute, which the caller
 * writes. Returns the measurement, for a caller whose server answer can change under it.
 */
export function useOverflowsBlock(
  ref: RefObject<HTMLElement | null>,
  limitPx: number,
  report: (overflows: boolean) => void
): () => void {
  const measure = useCallback(() => {
    const element = ref.current;
    /* Released (735px up): the stylesheet ignores the answer, so keep the
       server's and skip the forced layout. The ResizeObserver re-asks when a
       narrowing viewport brings the clamp back. */
    if (!element || clampReleased()) {
      return;
    }
    /* An unlaid-out element (display:none, or jsdom, which lays nothing out)
       reports 0, which would read as "fits" and take the control away from
       every pack. Nothing is known yet, so nothing changes. */
    if (element.scrollHeight === 0) {
      return;
    }
    report(element.scrollHeight > limitPx);
  }, [ref, limitPx, report]);

  useEffect(() => {
    measure();

    /* Guarded because jsdom ships no ResizeObserver, so a consumer's test would throw on mount. Not
       a browser-support guard. */
    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(() => measure()) : undefined;
    if (observer && ref.current) {
      observer.observe(ref.current);
    }

    /* The observer alone is not enough, and the reason is the clamp itself: while the box is held
       at `limitPx` its own size stops changing, so content growing inside it fires nothing. */
    const element = ref.current;
    const onLoad = frameBatched(measure);
    element?.addEventListener("load", onLoad.schedule, true);

    /* Stale by the time it resolves if the component has gone; the flag is
       cheaper than an AbortController for one promise. */
    let live = true;
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    if (fonts && typeof fonts.ready?.then === "function") {
      void fonts.ready.then(() => {
        if (live) {
          measure();
        }
      });
    }

    return () => {
      live = false;
      observer?.disconnect();
      element?.removeEventListener("load", onLoad.schedule, true);
      onLoad.cancel();
    };
  }, [ref, measure]);

  return measure;
}
